import { useState, useMemo, useCallback } from "react";
import { Link, useSearchParams, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ProductCard from "@/components/base/ProductCard";
import PriceRangeSlider from "@/components/base/PriceRangeSlider";
import ScrollToTop from "@/components/base/ScrollToTop";
import QuickViewModal from "./components/QuickViewModal";
import { allProducts } from "@/mocks/products";
import { categories } from "@/mocks/categories";
import type { ProductDetail } from "@/mocks/products";

type SortOption = "newest" | "price-low" | "price-high" | "rating";

export default function ProductsPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const [searchParams] = useSearchParams();
  const categorySlug = useParams().category;

  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [selectedCategory, setSelectedCategory] = useState<string>(categorySlug || "");
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 300]);
  const [minRating, setMinRating] = useState(0);
  const [sortBy, setSortBy] = useState<SortOption>("newest");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState<ProductDetail | null>(null);
  const [visibleCount, setVisibleCount] = useState(9);
  const loadMoreIncrement = 9;

  const resolvedCategoryName = useMemo(() => {
    if (!selectedCategory) return t("nav.products");
    const cat = categories.find((c) => c.slug === selectedCategory);
    return cat ? (isAr ? cat.nameAr : cat.nameEn) : selectedCategory;
  }, [selectedCategory, isAr, t]);

  // ── Filtering ────────────────────────────────────────────
  const filteredProducts = useMemo(() => {
    let result = [...allProducts];

    if (selectedCategory) {
      const cat = categories.find((c) => c.slug === selectedCategory);
      if (cat) {
        result = result.filter((p) => p.categoryId === cat.id);
      }
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.nameEn.toLowerCase().includes(q) ||
          p.nameAr.includes(q) ||
          p.tags.some((tag) => tag.toLowerCase().includes(q)) ||
          p.descriptionEn.toLowerCase().includes(q),
      );
    }

    result = result.filter(
      (p) => p.price >= priceRange[0] && p.price <= priceRange[1],
    );

    if (minRating > 0) {
      result = result.filter((p) => p.rating >= minRating);
    }

    if (inStockOnly) {
      result = result.filter((p) => p.inStock);
    }

    switch (sortBy) {
      case "price-low":
        result.sort((a, b) => a.price - b.price);
        break;
      case "price-high":
        result.sort((a, b) => b.price - a.price);
        break;
      case "rating":
        result.sort((a, b) => b.rating - a.rating);
        break;
      case "newest":
      default:
        result.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
    }

    return result;
  }, [selectedCategory, searchQuery, priceRange, minRating, sortBy, inStockOnly]);

  const displayedProducts = filteredProducts.slice(0, visibleCount);
  const hasMoreProducts = visibleCount < filteredProducts.length;

  // Reset visible count when filters change
  useMemo(() => {
    setVisibleCount(9);
  }, [selectedCategory, searchQuery, priceRange, minRating, sortBy, inStockOnly]);

  // ── Filter helpers ───────────────────────────────────────
  const hasMinPriceChanged = priceRange[0] > 0;
  const hasMaxPriceChanged = priceRange[1] < 300;
  const hasPriceFilter = hasMinPriceChanged || hasMaxPriceChanged;
  const hasCategoryFilter = Boolean(selectedCategory);
  const hasSearchFilter = Boolean(searchQuery.trim());
  const hasRatingFilter = minRating > 0;
  const hasStockFilter = inStockOnly;

  const activeFilterCount = [
    hasCategoryFilter,
    hasSearchFilter,
    hasPriceFilter,
    hasRatingFilter,
    hasStockFilter,
  ].filter(Boolean).length;

  const clearAllFilters = useCallback(() => {
    setSelectedCategory("");
    setSearchQuery("");
    setPriceRange([0, 300]);
    setMinRating(0);
    setInStockOnly(false);
  }, []);

  const removeFilter = useCallback((type: string) => {
    switch (type) {
      case "category":
        setSelectedCategory("");
        break;
      case "search":
        setSearchQuery("");
        break;
      case "price":
        setPriceRange([0, 300]);
        break;
      case "rating":
        setMinRating(0);
        break;
      case "stock":
        setInStockOnly(false);
        break;
    }
  }, []);

  // ── Filter sidebar content ───────────────────────────────
  const FilterSidebar = () => (
    <div className="space-y-7">
      {/* Search */}
      <div>
        <h4 className="text-sm font-semibold text-foreground-900 mb-3">
          {t("products.search_label")}
        </h4>
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("nav.search_placeholder")}
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
          />
        </div>
      </div>

      {/* Categories */}
      <div>
        <h4 className="text-sm font-semibold text-foreground-900 mb-3">
          {t("products.category_label")}
        </h4>
        <div className="space-y-0.5">
          <button
            onClick={() => setSelectedCategory("")}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap ${
              !selectedCategory
                ? "bg-primary-50 text-primary-700 font-medium"
                : "text-foreground-600 hover:bg-background-100"
            }`}
          >
            {t("nav.products")}
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.slug)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-between ${
                selectedCategory === cat.slug
                  ? "bg-primary-50 text-primary-700 font-medium"
                  : "text-foreground-600 hover:bg-background-100"
              }`}
            >
              <span>{isAr ? cat.nameAr : cat.nameEn}</span>
              <span className="text-xs text-foreground-400">{cat.itemCount}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Price Range */}
      <div>
        <h4 className="text-sm font-semibold text-foreground-900 mb-3">
          {t("products.price_label")}
        </h4>
        <PriceRangeSlider
          min={0}
          max={300}
          value={priceRange}
          onChange={setPriceRange}
          step={1}
          formatLabel={(v) => `$${v}`}
        />
      </div>

      {/* Rating */}
      <div>
        <h4 className="text-sm font-semibold text-foreground-900 mb-3">
          {t("products.rating_label")}
        </h4>
        <div className="space-y-1.5">
          {[4, 3, 2, 1].map((rating) => (
            <button
              key={rating}
              onClick={() => setMinRating(minRating === rating ? 0 : rating)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                minRating === rating
                  ? "bg-accent-50 text-accent-700 font-medium"
                  : "text-foreground-600 hover:bg-background-100"
              }`}
            >
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <i
                    key={i}
                    className={`text-xs ${i < rating ? "ri-star-fill text-accent-500" : "ri-star-line text-foreground-300"}`}
                  ></i>
                ))}
              </div>
              <span className="text-xs text-foreground-500">&amp; {t("products.rating_and_up")}</span>
            </button>
          ))}
        </div>
      </div>

      {/* In Stock Toggle */}
      <div>
        <button
          onClick={() => setInStockOnly(!inStockOnly)}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
            inStockOnly
              ? "bg-secondary-50 text-secondary-700 font-medium"
              : "text-foreground-600 hover:bg-background-100"
          }`}
        >
          <div
            className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
              inStockOnly
                ? "bg-secondary-500 border-secondary-500"
                : "border-background-300"
            }`}
          >
            {inStockOnly && <i className="ri-check-line text-white text-xs"></i>}
          </div>
          <span>{t("products.in_stock_only")}</span>
        </button>
      </div>

      {/* Clear All */}
      {activeFilterCount > 0 && (
        <button
          onClick={clearAllFilters}
          className="w-full py-2.5 text-sm font-medium text-foreground-600 hover:text-foreground-900 border border-background-200 rounded-lg hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          {t("products.clear_all")} ({activeFilterCount})
        </button>
      )}
    </div>
  );

  // ── Filter tags ──────────────────────────────────────────
  const FilterTags = () => {
    if (activeFilterCount === 0) return null;

    return (
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {hasCategoryFilter && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-50 text-primary-700 text-xs font-medium rounded-full">
            {resolvedCategoryName}
            <button onClick={() => removeFilter("category")} className="cursor-pointer hover:text-primary-900">
              <i className="ri-close-line"></i>
            </button>
          </span>
        )}
        {hasSearchFilter && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-foreground-100 text-foreground-700 text-xs font-medium rounded-full">
            &ldquo;{searchQuery}&rdquo;
            <button onClick={() => removeFilter("search")} className="cursor-pointer hover:text-foreground-900">
              <i className="ri-close-line"></i>
            </button>
          </span>
        )}
        {hasPriceFilter && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-foreground-100 text-foreground-700 text-xs font-medium rounded-full">
            {t("products.tag_price", { min: priceRange[0], max: priceRange[1] })}
            <button onClick={() => removeFilter("price")} className="cursor-pointer hover:text-foreground-900">
              <i className="ri-close-line"></i>
            </button>
          </span>
        )}
        {hasRatingFilter && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-50 text-accent-700 text-xs font-medium rounded-full">
            {minRating}+ <i className="ri-star-fill text-xs"></i>
            <button onClick={() => removeFilter("rating")} className="cursor-pointer hover:text-accent-900">
              <i className="ri-close-line"></i>
            </button>
          </span>
        )}
        {hasStockFilter && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-secondary-50 text-secondary-700 text-xs font-medium rounded-full">
            {t("products.in_stock_only")}
            <button onClick={() => removeFilter("stock")} className="cursor-pointer hover:text-secondary-900">
              <i className="ri-close-line"></i>
            </button>
          </span>
        )}
        {activeFilterCount > 1 && (
          <button
            onClick={clearAllFilters}
            className="text-xs text-foreground-500 hover:text-foreground-800 underline cursor-pointer whitespace-nowrap"
          >
            {t("products.clear_all")}
          </button>
        )}
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background-50" dir={isAr ? "rtl" : "ltr"}>
      <Navbar />
      <main className="pt-20">
        {/* Header */}
        <div className="bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-6 lg:px-8 py-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 text-sm text-foreground-500 mb-2">
                <Link to="/" className="hover:text-foreground-800 transition-colors">
                  {t("nav.home")}
                </Link>
                <span>/</span>
                <span className="text-foreground-900 font-medium">{resolvedCategoryName}</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
                {resolvedCategoryName}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                {t(
                  filteredProducts.length === 1
                    ? "products.results_count"
                    : "products.results_count_plural",
                  { count: filteredProducts.length },
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="w-full px-4 md:px-6 lg:px-8 py-8">
          <div className="max-w-7xl mx-auto">
            <div className={`flex gap-8 ${isAr ? "flex-row-reverse" : ""}`}>
              {/* Desktop Sidebar */}
              <aside className="hidden lg:block w-64 flex-shrink-0">
                <div className="sticky top-24">
                  <FilterSidebar />
                </div>
              </aside>

              {/* Products Area */}
              <div className="flex-1 min-w-0">
                {/* Top bar: mobile filter button, filter tags, sort */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-2">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setMobileFiltersOpen(true)}
                      className="lg:hidden flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground-700 bg-background-100 border border-background-200 rounded-lg hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-equalizer-line"></i>
                      {t("products.filters")}
                      {activeFilterCount > 0 && (
                        <span className="w-5 h-5 bg-primary-500 text-white text-xs rounded-full flex items-center justify-center font-semibold">
                          {activeFilterCount}
                        </span>
                      )}
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground-500 hidden sm:inline whitespace-nowrap">
                      {t("products.sort_by")}:
                    </span>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as SortOption)}
                      className="px-3 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                    >
                      <option value="newest">{t("products.sort_newest")}</option>
                      <option value="price-low">{t("products.sort_price_low")}</option>
                      <option value="price-high">{t("products.sort_price_high")}</option>
                      <option value="rating">{t("products.sort_rating")}</option>
                    </select>
                  </div>
                </div>

                {/* Filter Tags */}
                <FilterTags />

                {/* Product Grid */}
                {displayedProducts.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                      {displayedProducts.map((product) => (
                        <ProductCard
                          key={product.id}
                          {...product}
                          imageUrl={product.images[0]}
                          onQuickView={() => setQuickViewProduct(product)}
                        />
                      ))}
                    </div>

                    {/* Load More */}
                    <div className="mt-10 flex flex-col items-center gap-4">
                      <p className="text-sm text-foreground-500">
                        {t("products.showing_count", { shown: displayedProducts.length, total: filteredProducts.length })}
                      </p>
                      {hasMoreProducts && (
                        <button
                          onClick={() => setVisibleCount((v) => v + loadMoreIncrement)}
                          className="inline-flex items-center gap-2 px-8 py-3 bg-foreground-950 text-white font-semibold rounded-full hover:bg-foreground-900 transition-colors cursor-pointer text-sm whitespace-nowrap"
                        >
                          <i className="ri-add-line"></i>
                          {t("products.load_more")}
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  /* Empty State */
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                      <i className="ri-search-line text-2xl text-foreground-400"></i>
                    </div>
                    <h3 className="text-lg font-medium text-foreground-900 mb-2">
                      {t("products.no_products")}
                    </h3>
                    <p className="text-sm text-foreground-500 mb-6">
                      {t("products.no_products_desc")}
                    </p>
                    <button
                      onClick={clearAllFilters}
                      className="px-6 py-2.5 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {t("products.clear_all")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Filters Drawer */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileFiltersOpen(false)}
          ></div>
          <div
            className={`absolute top-0 ${isAr ? "left-0" : "right-0"} h-full w-80 bg-background-50 overflow-y-auto`}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-foreground-900">
                  {t("products.filters")}
                </h3>
                <button
                  onClick={() => setMobileFiltersOpen(false)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <FilterSidebar />
              <div className="mt-8 pt-6 border-t border-background-200">
                <button
                  onClick={() => setMobileFiltersOpen(false)}
                  className="w-full py-3 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors cursor-pointer text-sm whitespace-nowrap"
                >
                  {t("products.show_results", { count: filteredProducts.length })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
      <ScrollToTop />

      {/* Quick View Modal */}
      {quickViewProduct && (
        <QuickViewModal
          product={quickViewProduct}
          onClose={() => setQuickViewProduct(null)}
        />
      )}
    </div>
  );
}